import { Close, RequestQuoteOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { UseProposalReturn } from "../hooks/useProposal";
import { ProposalStatus } from "../types/proposal";
import ClientCard from "./ClientCard";
import DiscountCard from "./DiscountCard";
import FreightCard from "./FreightCard";
import ProductsCard from "./ProductsCard";
import ProposalActions from "./ProposalActions";
import ProposalTrackingCard from "./ProposalTrackingCard";
import SummaryCard from "./SummaryCard";
import WhatsappPreview from "./WhatsappPreview";

type Props = {
  open: boolean;
  proposal: UseProposalReturn;
  proposalId: string | null;
  status: ProposalStatus;
  canSave: boolean;
  onClose: () => void;
  onStatusChange: (status: ProposalStatus) => void;
  onSave: () => void;
  onCopyNotion: () => void;
  onCalculateFreight: () => void;
  onFeedback: (message: string, severity: "success" | "error" | "info") => void;
};

const ProposalFormDialog: React.FC<Props> = ({
  open,
  proposal,
  proposalId,
  status,
  canSave,
  onClose,
  onStatusChange,
  onSave,
  onCopyNotion,
  onCalculateFreight,
  onFeedback,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const hasSelectedProducts = proposal.products.some(
    product => product.selected,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      PaperProps={{ sx: { height: { sm: "94vh" } } }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <RequestQuoteOutlined color="primary" />
            <div>
              <Typography variant="h6">
                {proposalId
                  ? `Editar ${proposalId}`
                  : "Nova proposta comercial"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preencha os dados e acompanhe a mensagem em tempo real
              </Typography>
            </div>
          </Stack>
          <IconButton aria-label="Fechar proposta" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "action.hover" }}>
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} lg={7}>
              <Stack spacing={2}>
                <ClientCard
                  client={proposal.client}
                  onChange={proposal.updateClient}
                  onPatch={proposal.patchClient}
                />
                <ProductsCard
                  products={proposal.products}
                  onToggle={proposal.toggleProduct}
                  onQuantityChange={proposal.updateProductQuantity}
                />
                <FreightCard
                  freight={proposal.freight}
                  onChange={proposal.updateFreight}
                  onCalculate={onCalculateFreight}
                />
                <DiscountCard
                  type={proposal.discountType}
                  value={proposal.discountValue}
                  onTypeChange={proposal.setDiscountType}
                  onValueChange={proposal.setDiscountValue}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Stack
                spacing={2}
                sx={{ position: { lg: "sticky" }, top: { lg: 0 } }}
              >
                <SummaryCard totals={proposal.totals} />
                <ProposalTrackingCard
                  proposalId={proposalId}
                  status={status}
                  canSave={canSave}
                  onStatusChange={onStatusChange}
                  onSave={onSave}
                  onCopyNotion={onCopyNotion}
                />
                <WhatsappPreview message={proposal.message} />
                <ProposalActions
                  message={proposal.message}
                  canShare={hasSelectedProducts}
                  onFeedback={onFeedback}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalFormDialog;
